import { useState, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, Image, Eye, Megaphone, X, Upload, Loader2, Pencil, Globe, Link2 } from "lucide-react";
import {
  getAdminAnnouncements,
  createAnnouncement,
  updateAnnouncement,
  deleteAnnouncement,
  uploadAnnouncementImage,
  type Announcement,
} from "../../services/AnnouncementService";

type FormMode = { type: "create" } | { type: "edit"; announcement: Announcement };

export default function AnnouncementsTab() {
  const queryClient = useQueryClient();
  const [formMode, setFormMode] = useState<FormMode | null>(null);
  const [previewAnnouncement, setPreviewAnnouncement] = useState<Announcement | null>(null);
  const [previewLang, setPreviewLang] = useState<"en" | "es">("en");
  const [formLang, setFormLang] = useState<"en" | "es">("en");
  const [formData, setFormData] = useState({ title: "", body: "", titleEs: "", bodyEs: "", imageUrl: "", link: "", popUp: false });
  const [uploading, setUploading] = useState(false);
  const [showFormPreview, setShowFormPreview] = useState(false);
  const [formPreviewLang, setFormPreviewLang] = useState<"en" | "es">("en");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["admin-announcements"],
    queryFn: () => getAdminAnnouncements(50),
  });

  const createMutation = useMutation({
    mutationFn: createAnnouncement,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-announcements"] });
      closeForm();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ createdAt, data }: { createdAt: number; data: typeof formData }) => updateAnnouncement(createdAt, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-announcements"] });
      closeForm();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteAnnouncement,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin-announcements"] }),
  });

  const openCreateForm = () => {
    setFormData({ title: "", body: "", titleEs: "", bodyEs: "", imageUrl: "", link: "", popUp: false });
    setFormLang("en");
    setFormMode({ type: "create" });
  };

  const openEditForm = (announcement: Announcement) => {
    setFormData({
      title: announcement.title,
      body: announcement.body,
      titleEs: announcement.titleEs || "",
      bodyEs: announcement.bodyEs || "",
      imageUrl: announcement.imageUrl || "",
      link: announcement.link || "",
      popUp: announcement.popUp || false,
    });
    setFormLang("en");
    setFormMode({ type: "edit", announcement });
  };

  const closeForm = () => {
    setFormMode(null);
    setFormData({ title: "", body: "", titleEs: "", bodyEs: "", imageUrl: "", link: "", popUp: false });
    setShowFormPreview(false);
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const imageUrl = await uploadAnnouncementImage(file);
      setFormData((prev) => ({ ...prev, imageUrl }));
    } catch (error) {
      console.error("Failed to upload image:", error);
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!formData.title || !formData.body) return;
    
    if (formMode?.type === "edit") {
      updateMutation.mutate({ createdAt: formMode.announcement.createdAt, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString("en-US", {
      month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  };

  const isSubmitting = createMutation.isPending || updateMutation.isPending;
  const isEditing = formMode?.type === "edit";

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div className="leading-tight">
          <h2 className="text-[13px] font-bold text-gray-900 dark:text-white">Announcements</h2>
          <p className="text-[11px] text-gray-400 dark:text-dark-400">
            <span className="font-mono tabular-nums">{data?.announcements.length || 0}</span> total
          </p>
        </div>
        <button
          onClick={openCreateForm}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-900 dark:bg-white text-white dark:text-dark-900 hover:opacity-90 transition-opacity"
        >
          <Plus className="w-3.5 h-3.5" />
          New announcement
        </button>
      </div>

      {/* Create/Edit Form Modal */}
      {formMode && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`bg-white dark:bg-dark-800 rounded-xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col relative border border-gray-200/80 dark:border-dark-700 shadow-2xl shadow-black/10 dark:shadow-black/40 ${showFormPreview ? "rounded-2xl" : ""}`}>
            {!showFormPreview && (
              <div className="flex items-center justify-between p-4 border-b dark:border-dark-700 flex-shrink-0">
                <h3 className="text-lg font-semibold dark:text-white">
                  {isEditing ? "Edit Announcement" : "Create Announcement"}
                </h3>
                <button onClick={closeForm} className="p-1 hover:bg-gray-100 dark:hover:bg-dark-700 rounded-lg dark:text-dark-300">
                  <X className="w-5 h-5" />
                </button>
              </div>
            )}
            {showFormPreview ? (
              /* Preview View - Exact popup modal replica */
              <div className="flex flex-col max-h-[80vh] bg-white dark:bg-dark-800">
                {/* Popup Header */}
                <div className="flex-shrink-0 bg-primary-900 dark:bg-dark-900 rounded-t-2xl">
                  <div className="flex items-center justify-between px-4 pt-4 pb-3">
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={closeForm}
                        className="p-1.5 -ml-1.5 hover:bg-white/10 rounded-lg transition-colors"
                      >
                        <X className="w-5 h-5 text-white" />
                      </button>
                      <img src="/logo_white.svg" alt="DealHawk" className="h-5" />
                    </div>
                    <div className="flex bg-white/10 rounded-lg p-0.5">
                      <button
                        type="button"
                        onClick={() => setFormPreviewLang("en")}
                        className={`px-2 py-1 rounded-md text-xs transition-colors ${
                          formPreviewLang === "en" ? "bg-white text-primary-900 shadow-sm" : "text-white/70 hover:text-white"
                        }`}
                      >🇺🇸</button>
                      <button
                        type="button"
                        onClick={() => setFormPreviewLang("es")}
                        className={`px-2 py-1 rounded-md text-xs transition-colors ${
                          formPreviewLang === "es" ? "bg-white text-primary-900 shadow-sm" : "text-white/70 hover:text-white"
                        }`}
                      >🇪🇸</button>
                    </div>
                  </div>
                </div>

                {/* Scrollable content */}
                <div className="flex-1 overflow-y-auto bg-white dark:bg-dark-800">
                  <div className="p-5">
                    <div className="flex gap-4 items-start">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-gray-400 dark:text-dark-400 mb-2">
                          {new Date().toLocaleDateString(formPreviewLang === "es" ? "es-ES" : "en-US", { month: "short", day: "numeric" })}
                        </p>
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4 leading-snug">
                          {formPreviewLang === "es" && formData.titleEs ? formData.titleEs : formData.title || "Untitled"}
                        </h2>
                        <div className="text-gray-700 dark:text-dark-300 text-[15px] leading-relaxed space-y-3">
                          {(formPreviewLang === "es" && formData.bodyEs ? formData.bodyEs : formData.body || "No content").split('\n\n').map((p, i) => (
                            <p key={i} className="whitespace-pre-wrap">{p}</p>
                          ))}
                        </div>
                        {formData.link && (
                          <a href={formData.link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm font-medium text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 break-all mt-4">
                            <Link2 className="w-4 h-4 flex-shrink-0" />
                            {formData.link}
                          </a>
                        )}
                        {formPreviewLang === "es" && (!formData.titleEs || !formData.bodyEs) && (
                          <p className="text-xs text-amber-600 dark:text-amber-400 mt-4 flex items-center gap-1">
                            <Globe className="w-3 h-3" /> Falling back to English
                          </p>
                        )}
                      </div>
                      {formData.imageUrl && (
                        <img src={formData.imageUrl} alt="" className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl object-contain bg-white dark:bg-dark-700 border border-gray-200 dark:border-dark-600 flex-shrink-0" />
                      )}
                    </div>
                  </div>
                </div>

                {/* Admin controls */}
                <div className="flex-shrink-0 px-5 pb-5 pt-4 bg-white dark:bg-dark-800 border-t border-gray-100 dark:border-dark-700">
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setShowFormPreview(false)}
                      className="flex-1 px-4 py-2 border border-gray-200 dark:border-dark-600 rounded-lg bg-white dark:bg-dark-700 text-gray-700 dark:text-dark-200 hover:bg-gray-50 dark:hover:bg-dark-600 transition-colors text-sm font-medium"
                    >
                      Back to Edit
                    </button>
                    <button
                      type="button"
                      onClick={handleSubmit}
                      disabled={isSubmitting || !formData.title || !formData.body}
                      className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium disabled:opacity-50"
                    >
                      {isSubmitting ? (isEditing ? "Saving..." : "Creating...") : (isEditing ? "Save Changes" : "Post Announcement")}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              /* Form View */
              <>
                {/* Language Toggle */}
                <div className="px-4 pt-4">
                  <div className="flex bg-gray-100 dark:bg-dark-700 rounded-lg p-1">
                    <button
                      type="button"
                      onClick={() => setFormLang("en")}
                      className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                        formLang === "en" ? "bg-white dark:bg-dark-600 text-gray-900 dark:text-white shadow-sm" : "text-gray-600 dark:text-dark-400 hover:text-gray-900 dark:hover:text-white"
                      }`}
                    >
                      <span className="text-base">🇺🇸</span> English
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormLang("es")}
                      className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                        formLang === "es" ? "bg-white dark:bg-dark-600 text-gray-900 dark:text-white shadow-sm" : "text-gray-600 dark:text-dark-400 hover:text-gray-900 dark:hover:text-white"
                      }`}
                    >
                      <span className="text-base">🇪🇸</span> Spanish
                    </button>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="p-4 space-y-4">
                  {formLang === "en" ? (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-dark-300 mb-1">Title (English)</label>
                        <input
                          type="text"
                          value={formData.title}
                          onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
                          className="w-full px-3 py-2 border dark:border-dark-600 rounded-lg bg-white dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 focus:border-red-500"
                          placeholder="Announcement title..."
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-dark-300 mb-1">Body (English)</label>
                        <textarea
                          value={formData.body}
                          onChange={(e) => setFormData((prev) => ({ ...prev, body: e.target.value }))}
                          className="w-full px-3 py-2 border dark:border-dark-600 rounded-lg bg-white dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 focus:border-red-500 min-h-[120px]"
                          placeholder="Write your announcement..."
                          required
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-dark-300 mb-1">Title (Spanish) <span className="text-gray-400 dark:text-dark-500 font-normal">- optional</span></label>
                        <input
                          type="text"
                          value={formData.titleEs}
                          onChange={(e) => setFormData((prev) => ({ ...prev, titleEs: e.target.value }))}
                          className="w-full px-3 py-2 border dark:border-dark-600 rounded-lg bg-white dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 focus:border-red-500"
                          placeholder="Título del anuncio..."
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-dark-300 mb-1">Body (Spanish) <span className="text-gray-400 dark:text-dark-500 font-normal">- optional</span></label>
                        <textarea
                          value={formData.bodyEs}
                          onChange={(e) => setFormData((prev) => ({ ...prev, bodyEs: e.target.value }))}
                          className="w-full px-3 py-2 border dark:border-dark-600 rounded-lg bg-white dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 focus:border-red-500 min-h-[120px]"
                          placeholder="Escribe tu anuncio..."
                        />
                      </div>
                    </>
                  )}
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-dark-300 mb-1">Image (optional)</label>
                    <input type="file" ref={fileInputRef} onChange={handleImageUpload} accept="image/*" className="hidden" />
                    {formData.imageUrl ? (
                      <div className="relative">
                        <img src={formData.imageUrl} alt="Preview" className="w-full h-40 object-cover rounded-lg" />
                        <button
                          type="button"
                          onClick={() => setFormData((prev) => ({ ...prev, imageUrl: "" }))}
                          className="absolute top-2 right-2 p-1 bg-red-600 text-white rounded-full hover:bg-red-700"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        className="w-full py-8 border-2 border-dashed dark:border-dark-600 rounded-lg hover:border-red-500 dark:hover:border-red-500 transition-colors flex flex-col items-center gap-2 text-gray-500 dark:text-dark-400"
                      >
                        {uploading ? <Loader2 className="w-6 h-6 animate-spin" /> : <Upload className="w-6 h-6" />}
                        <span className="text-sm">{uploading ? "Uploading..." : "Click to upload image"}</span>
                      </button>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-dark-300 mb-1">Link (optional)</label>
                    <input
                      type="url"
                      value={formData.link}
                      onChange={(e) => setFormData((prev) => ({ ...prev, link: e.target.value }))}
                      className="w-full px-3 py-2 border dark:border-dark-600 rounded-lg bg-white dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      placeholder="https://..."
                    />
                  </div>
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="popUp"
                      checked={formData.popUp}
                      onChange={(e) => setFormData((prev) => ({ ...prev, popUp: e.target.checked }))}
                      className="w-4 h-4 text-red-600 rounded focus:ring-red-500"
                    />
                    <label htmlFor="popUp" className="text-sm text-gray-700 dark:text-dark-300">
                      Show as popup for users who haven't seen it
                    </label>
                  </div>
                  <div className="flex gap-3 pt-2">
                    <button
                      type="button"
                      onClick={closeForm}
                      className="flex-1 px-4 py-2 border dark:border-dark-600 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors text-sm font-medium dark:text-dark-300"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowFormPreview(true)}
                      disabled={!formData.title || !formData.body}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-gray-800 dark:bg-dark-600 text-white rounded-lg hover:bg-gray-900 dark:hover:bg-dark-500 transition-colors text-sm font-medium disabled:opacity-50"
                    >
                      <Eye className="w-4 h-4" />
                      Preview
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {previewAnnouncement && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-dark-800 rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b dark:border-dark-700">
              <div className="flex items-center gap-3">
                <h3 className="text-lg font-semibold dark:text-white">Preview</h3>
                {/* Language toggle for preview */}
                <div className="flex bg-gray-100 dark:bg-dark-700 rounded-lg p-0.5">
                  <button
                    onClick={() => setPreviewLang("en")}
                    className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                      previewLang === "en" ? "bg-white dark:bg-dark-600 text-gray-900 dark:text-white shadow-sm" : "text-gray-600 dark:text-dark-400"
                    }`}
                  >
                    🇺🇸
                  </button>
                  <button
                    onClick={() => setPreviewLang("es")}
                    className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                      previewLang === "es" ? "bg-white dark:bg-dark-600 text-gray-900 dark:text-white shadow-sm" : "text-gray-600 dark:text-dark-400"
                    }`}
                  >
                    🇪🇸
                  </button>
                </div>
              </div>
              <button onClick={() => setPreviewAnnouncement(null)} className="p-1 hover:bg-gray-100 dark:hover:bg-dark-700 rounded-lg dark:text-dark-300">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4">
              {previewAnnouncement.imageUrl && (
                <img src={previewAnnouncement.imageUrl} alt="" className="w-full h-48 object-cover rounded-lg mb-4" />
              )}
              {previewAnnouncement.link && (
                <a href={previewAnnouncement.link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm font-medium text-red-600 hover:text-red-700 dark:text-red-400 mb-4 break-all">
                  <Link2 className="w-4 h-4 flex-shrink-0" />
                  {previewAnnouncement.link}
                </a>
              )}
              <h4 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                {previewLang === "es" && previewAnnouncement.titleEs ? previewAnnouncement.titleEs : previewAnnouncement.title}
              </h4>
              <p className="text-gray-600 dark:text-dark-300 whitespace-pre-wrap">
                {previewLang === "es" && previewAnnouncement.bodyEs ? previewAnnouncement.bodyEs : previewAnnouncement.body}
              </p>
              {previewLang === "es" && (!previewAnnouncement.titleEs || !previewAnnouncement.bodyEs) && (
                <p className="text-xs text-amber-600 dark:text-amber-400 mt-3 flex items-center gap-1">
                  <Globe className="w-3 h-3" /> Falling back to English (no Spanish translation)
                </p>
              )}
              <div className="mt-4 pt-4 border-t dark:border-dark-700 text-sm text-gray-500 dark:text-dark-400">
                {formatDate(previewAnnouncement.createdAt)}
                {previewAnnouncement.popUp && <span className="ml-2 px-2 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded-full text-xs">Popup</span>}
                {previewAnnouncement.titleEs && <span className="ml-2 px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-full text-xs">ES</span>}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Announcements List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400 dark:text-dark-400" />
        </div>
      ) : data?.announcements.length === 0 ? (
        <div className="text-center py-14 bg-white dark:bg-dark-800 rounded-2xl ring-1 ring-gray-200 dark:ring-dark-700 shadow-sm">
          <Megaphone className="w-8 h-8 text-gray-300 dark:text-dark-500 mx-auto mb-3" />
          <p className="text-sm font-medium text-gray-600 dark:text-dark-200">No announcements yet</p>
          <p className="text-xs text-gray-400 dark:text-dark-400 mt-1">Create your first announcement to notify users</p>
        </div>
      ) : (
        <div className="bg-white dark:bg-dark-800 rounded-2xl ring-1 ring-gray-200 dark:ring-dark-700 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100 dark:border-dark-700">
                  <th className="text-left px-4 py-2 text-[10px] font-bold uppercase tracking-[0.14em] text-gray-400 dark:text-dark-400">Announcement</th>
                  <th className="text-left px-4 py-2 text-[10px] font-bold uppercase tracking-[0.14em] text-gray-400 dark:text-dark-400 hidden sm:table-cell">Created</th>
                  <th className="text-left px-4 py-2 text-[10px] font-bold uppercase tracking-[0.14em] text-gray-400 dark:text-dark-400 hidden md:table-cell">Flags</th>
                  <th className="text-right px-4 py-2 text-[10px] font-bold uppercase tracking-[0.14em] text-gray-400 dark:text-dark-400" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50 dark:divide-dark-700/60">
                {data?.announcements.map((announcement) => (
                  <tr key={announcement.createdAt} className="group hover:bg-gray-50 dark:hover:bg-dark-700/40 transition-colors">
                    <td className="px-4 py-2 max-w-[340px]">
                      <div className="flex items-center gap-2.5 min-w-0">
                        {announcement.imageUrl ? (
                          <img src={announcement.imageUrl} alt="" className="w-7 h-7 rounded-md object-cover shrink-0" />
                        ) : (
                          <div className="w-7 h-7 bg-gray-100 dark:bg-dark-700 rounded-md flex items-center justify-center shrink-0">
                            <Image className="w-3.5 h-3.5 text-gray-400 dark:text-dark-400" />
                          </div>
                        )}
                        <div className="min-w-0">
                          <p className="text-[13px] font-semibold text-gray-900 dark:text-white truncate">{announcement.title}</p>
                          <p className="text-[11px] text-gray-400 dark:text-dark-400 truncate">{announcement.body}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-2 text-xs text-gray-500 dark:text-dark-300 hidden sm:table-cell whitespace-nowrap" title={formatDate(announcement.createdAt)}>
                      {new Date(announcement.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                      <span className="text-gray-400 dark:text-dark-400"> · {new Date(announcement.createdAt).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}</span>
                    </td>
                    <td className="px-4 py-2 hidden md:table-cell">
                      <div className="flex gap-1">
                        {announcement.popUp && (
                          <span className="px-1.5 py-px rounded text-[11px] font-semibold bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200 dark:bg-amber-900/25 dark:text-amber-300 dark:ring-amber-800">Popup</span>
                        )}
                        {announcement.titleEs && (
                          <span className="px-1.5 py-px rounded text-[11px] font-semibold bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-200 dark:bg-blue-900/25 dark:text-blue-300 dark:ring-blue-800">ES</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex items-center justify-end gap-0.5">
                        <button
                          onClick={() => {
                            setPreviewLang("en");
                            setPreviewAnnouncement(announcement);
                          }}
                          className="p-1.5 rounded-md text-gray-400 dark:text-dark-400 hover:text-gray-700 dark:hover:text-dark-100 hover:bg-gray-100 dark:hover:bg-dark-600 transition-colors"
                          title="Preview"
                        >
                          <Eye className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => openEditForm(announcement)}
                          className="p-1.5 rounded-md text-gray-400 dark:text-dark-400 hover:text-gray-700 dark:hover:text-dark-100 hover:bg-gray-100 dark:hover:bg-dark-600 transition-colors"
                          title="Edit"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => {
                            if (confirm("Are you sure you want to delete this announcement?")) {
                              deleteMutation.mutate(announcement.createdAt);
                            }
                          }}
                          disabled={deleteMutation.isPending}
                          className="p-1.5 rounded-md text-gray-400 dark:text-dark-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/25 transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
